/**
 * tRPC client and typed options proxy for the Portals app.
 *
 * This module provides the `trpc` proxy — a fully-typed interface to all tRPC
 * procedures that produces TanStack Query options objects. Use it everywhere:
 *
 *   Route loaders:
 *     context.queryClient.ensureQueryData(trpc.portal.getAuthor.queryOptions({ portalId, locale }))
 *
 *   Components:
 *     useSuspenseQuery(trpc.portal.getAuthor.queryOptions({ portalId, locale }))
 *     useMutation(trpc.payments.preregisterDonation.mutationOptions())
 *
 *   Cache invalidation:
 *     queryClient.invalidateQueries({ queryKey: trpc.ngo.getProjects.queryKey() })
 *     queryClient.removeQueries({ queryKey: trpc.ngo.pathKey() })
 *
 * Transport:
 * - SSR: in-process via `callTRPCProcedure` (through a `createServerFn` boundary)
 * - Client: batched HTTP via `httpBatchLink` → `/api/trpc` endpoint
 */

import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import { QueryClient } from '@tanstack/react-query';
import type { TRPCLink } from '@trpc/client';
import type { AppRouter } from './trpc/router.server';
import { trpcSSRCallFn } from './trpc-server-fn';
import { telemetryHeaders } from './telemetry-headers';
import { syncVersionFromHeader } from './app-version';
import { MAX_BATCH_URL_LENGTH } from './trpc-batch';

// ── SSR link (in-process) ────────────────────────────────────────────────────

const isServer = typeof window === 'undefined';

/**
 * Terminating tRPC link for SSR: dispatches calls through `trpcSSRCallFn`,
 * a `createServerFn` whose handler runs `callTRPCProcedure` directly in-process.
 * The server function boundary is required so that `.server.ts` imports are
 * tree-shaken from the client bundle.
 */
const ssrLink: TRPCLink<AppRouter> = () => {
	return ({ op }) => {
		return observable((observer) => {
			const type = op.type === 'subscription' ? 'query' : op.type;
			trpcSSRCallFn({ data: { path: op.path, input: op.input, type } })
				.then((result) => {
					observer.next({ result: { type: 'data', data: result } });
					observer.complete();
				})
				.catch((err: unknown) => {
					observer.error(
						err instanceof TRPCClientError
							? err
							: new TRPCClientError<AppRouter>(err instanceof Error ? err.message : String(err), {
									cause: err instanceof Error ? err : undefined,
								}),
					);
				});
		});
	};
};

// ── tRPC client ──────────────────────────────────────────────────────────────

export const trpcClient = createTRPCClient<AppRouter>({
	links: isServer
		? [ssrLink]
		: // `headers` is evaluated per request, not once at client construction — the viewport changes as
			// the user resizes and the trace id changes on every navigation, so a snapshot would go stale.
			//
			// `fetch` is wrapped only to read the deployed version back off the response. Server-function
			// calls already do that (`appVersionAwareFetch` in `start.ts`); tRPC is the other half, and the
			// only half a page that does nothing but poll queries — the live counter, left open on a screen
			// for weeks — ever exercises.
			[
				httpBatchLink({
					url: '/api/trpc',
					// A ceiling on the batched GET. Without one, tRPC will happily build a URL of any length:
					// a restored cache once refetched ~110 live-counter windows at once and produced a 16 KB
					// URL the service answered with a 500 (TEC-3807/TEC-3810). Over the cap tRPC splits the
					// batch into several requests instead, which every batched procedure benefits from — the
					// live counter's stable query key is the fix, this is the floor under it.
					maxURLLength: MAX_BATCH_URL_LENGTH,
					headers: () => telemetryHeaders(),
					fetch: (input, init) =>
						globalThis.fetch(input, init).then((response) => {
							syncVersionFromHeader(response);
							return response;
						}),
				}),
			],
});

// ── Options proxy (singleton) ────────────────────────────────────────────────

/**
 * Placeholder QueryClient used only to satisfy `createTRPCOptionsProxy`'s
 * required parameter. It is only accessed internally by tRPC for async
 * iterable (streaming) support, which we don't use. The actual QueryClient
 * is always provided by TanStack Router's context or `useQueryClient()`.
 */
const placeholderQueryClient = new QueryClient();

/**
 * Typed tRPC proxy for generating TanStack Query options.
 *
 * This is a singleton — safe to import in route loaders, components, and hooks.
 * It does NOT need React context.
 */
export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient: placeholderQueryClient,
});
